import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { useAppContext } from '@prisjakt/ui-app-context';
import Breadcrumb, { BreadcrumbPath } from '@prisjakt/ui-breadcrumb';
import { useViewportContext } from 'src/components/viewport/context';

import HeaderBlock from 'src/areas/product-collection/components/header-block';
import { SearchResultsNavigation } from '../navigation';
import { NoSearchResults } from 'src/components/no-search-results';
import { ProductResults } from './product-results';
import { PageContent } from 'src/components/page';
import { CategoryResults } from './category-results';
import { BrandResults } from './brand-results';
import { KeywordSuggestions } from '../keyword-suggestions';

import {
  trackKeywordSearchPageNoImages,
  trackKeywordSearchPageOffersOnlyVisited,
} from '../../tracking';

import * as S from './styles';

import { KeywordSearchPageQuery, Offer, Product } from 'src/types';

type Props = {
  data: KeywordSearchPageQuery;
};

const KeywordSearchPage = (props: Props) => {
  const { data } = props;
  const dispatch = useDispatch();
  const { t } = useAppContext();
  const { isMobile } = useViewportContext();

  useEffect(() => {
    dispatch({
      type: 'ADS_VIEW_UPDATE',
      view: `keywordLandingPage`,
    });
  }, [dispatch]);

  if (!data.keywordLandingPage) {
    return null;
  }

  const { popularSearches, keywordSuggestions: keywords } = data;
  const products = data.keywordLandingPage.newSearch?.results?.products;
  const categories = data.keywordLandingPage.newSearch?.results?.categories;
  const brands = data.keywordLandingPage.newSearch?.results?.brands;

  const productsCount = products?.pageInfo?.total || 0;
  const categoriesCount = categories?.pageInfo?.total || 0;
  const brandsCount = brands?.pageInfo?.total || 0;

  const noResults =
    (!products?.nodes?.length &&
      !categories?.nodes.length &&
      !brands?.nodes.length) ||
    !data.keywordLandingPage.published;

  const allProducts =
    (products?.nodes.filter(p => p?.__typename === 'Product') as Product[]) ||
    [];
  const allOffers =
    ((products?.nodes.filter(
      p => p?.__typename === 'Offer'
    ) as unknown) as Offer[]) || [];

  if (allProducts?.length === 0 && allOffers && allOffers.length >= 1) {
    trackKeywordSearchPageOffersOnlyVisited(
      data.keywordLandingPage!.title,
      data.keywordLandingPage!.url
    );
  }

  const noImagesOfferIDs = allOffers
    .map(offer => {
      // TODO: fix this hack
      if (!offer.media.first) return (offer as any).offerId;
      return null;
    })
    .filter(Boolean);

  const noImagesProductsIDs = allProducts
    .map(product => {
      if (!product.media.first) return product.id;
      return null;
    })
    .filter(Boolean);

  if (noImagesOfferIDs.length > 0 || noImagesProductsIDs.length > 0) {
    trackKeywordSearchPageNoImages(
      data.keywordLandingPage!.url,
      [...noImagesProductsIDs, ...noImagesOfferIDs].join(',')
    );
  }

  const brandNodes = brands?.nodes;

  return (
    <PageContent withAds data-test="KeywordSearchPage">
      <Breadcrumb
        homeText={t('common', 'breadcrumb.home')}
        path={(data.keywordLandingPage.newSearch?.results?.categories
          ?.parentCategory &&
        Array.isArray(
          data.keywordLandingPage.newSearch?.results?.categories?.parentCategory
            .path
        )
          ? (data.keywordLandingPage.newSearch?.results?.categories
              ?.parentCategory.path as BreadcrumbPath[])
          : []
        ).concat({
          id: data.keywordLandingPage.title,
          name:
            data.keywordLandingPage.title[0].toUpperCase() +
            data.keywordLandingPage.title.slice(1),
          pathName: '',
        })}
      />
      <HeaderBlock title={data.keywordLandingPage.title} />

      {keywords?.length ? (
        <KeywordSuggestions isMobile={isMobile} keywords={keywords} />
      ) : null}

      {noResults ? (
        <NoSearchResults popularSearches={popularSearches} />
      ) : (
        <>
          <SearchResultsNavigation
            brandsCount={brandsCount}
            categoriesCount={categoriesCount}
            productsCount={productsCount}
          />

          <S.SearchResults gap={2}>
            <CategoryResults
              categories={categories?.nodes}
              count={categoriesCount}
              wideContent={false}
            />

            <ProductResults
              facets={
                data.keywordLandingPage &&
                data.keywordLandingPage.newSearch &&
                data.keywordLandingPage.newSearch.filters
                  ? data.keywordLandingPage.newSearch.filters
                  : undefined
              }
              pageInfo={products?.pageInfo}
              products={products?.nodes || []}
              productsCount={productsCount}
              query={data.keywordLandingPage?.title}
              isWide={false}
            />

            {brandsCount > 0 && brandNodes && (
              <BrandResults brands={brandNodes} count={brandsCount} />
            )}
          </S.SearchResults>
        </>
      )}
    </PageContent>
  );
};

export default KeywordSearchPage;
